import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Type, Image, Video, MousePointer, FormInput, Timer, Quote, DollarSign,
  HelpCircle, Users, Minus, Grid2X2, Grid3X3, Undo2, Redo2, Monitor,
  Tablet, Smartphone, Save, Eye, ExternalLink, Trash2, GripVertical,
  Plus, X, ChevronLeft, Settings, Palette, Layout, Loader2, Check,
  AlignLeft, AlignCenter, AlignRight, Star, ChevronDown, MessageCircle,
  MapPin, ArrowLeftRight, ChevronRight, FileText, Copy, Globe, Clock,
  Menu, MoreVertical, Edit2, Folder
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { HexColorPicker } from 'react-colorful';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component definitions with 5 new components
const COMPONENT_LIBRARY = [
  { type: 'headline', name: 'Headline', icon: Type, category: 'text', defaultProps: { text: 'Your Headline Here', level: 'h1', align: 'center', color: '#ffffff', fontSize: 48 } },
  { type: 'paragraph', name: 'Paragraph', icon: AlignLeft, category: 'text', defaultProps: { text: 'Add your paragraph text here. Click to edit.', align: 'left', color: '#e5e7eb', fontSize: 16 } },
  { type: 'image', name: 'Image', icon: Image, category: 'media', defaultProps: { src: '', alt: 'Image', width: '100%', borderRadius: 8 } },
  { type: 'video', name: 'Video', icon: Video, category: 'media', defaultProps: { url: '', type: 'youtube', autoplay: false } },
  { type: 'button', name: 'Button', icon: MousePointer, category: 'action', defaultProps: { text: 'Click Here', url: '#', bgColor: '#10b981', textColor: '#ffffff', size: 'lg', fullWidth: false } },
  { type: 'form', name: 'Lead Form', icon: FormInput, category: 'action', defaultProps: { fields: ['name', 'email', 'phone'], buttonText: 'Submit', buttonColor: '#10b981' } },
  { type: 'whatsapp', name: 'WhatsApp Button', icon: MessageCircle, category: 'action', defaultProps: { phoneNumber: '', message: 'Hi! I am interested in your services.', buttonText: 'Chat on WhatsApp', buttonStyle: 'floating' } },
  { type: 'countdown', name: 'Countdown', icon: Timer, category: 'conversion', defaultProps: { targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), label: 'Offer ends in' } },
  { type: 'testimonial', name: 'Testimonial', icon: Quote, category: 'social', defaultProps: { quote: '"This product changed my life!"', author: 'Happy Customer', role: 'CEO, Company', avatar: '', rating: 5 } },
  { type: 'testimonialCarousel', name: 'Testimonial Carousel', icon: ChevronRight, category: 'social', defaultProps: { testimonials: [
    { quote: '"Amazing service! Highly recommend."', author: 'Sarah M.', role: 'Marketing Director', rating: 5 },
    { quote: '"Best investment I ever made."', author: 'Raj K.', role: 'Business Owner', rating: 5 },
    { quote: '"Transformed our business completely."', author: 'Emily C.', role: 'CEO', rating: 5 }
  ], autoplay: true, interval: 5 } },
  { type: 'pricing', name: 'Pricing', icon: DollarSign, category: 'conversion', defaultProps: { plans: [{ name: 'Basic', price: '₹999', features: ['Feature 1', 'Feature 2'] }] } },
  { type: 'faq', name: 'FAQ', icon: HelpCircle, category: 'content', defaultProps: { items: [{ question: 'How does this work?', answer: 'It works like magic!' }] } },
  { type: 'socialproof', name: 'Social Proof', icon: Users, category: 'social', defaultProps: { count: '10,000+', label: 'Happy Customers', logos: [] } },
  { type: 'googlemaps', name: 'Google Maps', icon: MapPin, category: 'media', defaultProps: { embedUrl: '', height: 400 } },
  { type: 'beforeafter', name: 'Before/After', icon: ArrowLeftRight, category: 'media', defaultProps: { beforeImage: '', afterImage: '', beforeLabel: 'Before', afterLabel: 'After' } },
  { type: 'divider', name: 'Divider', icon: Minus, category: 'layout', defaultProps: { height: 1, color: '#374151', margin: 32 } },
  { type: 'spacer', name: 'Spacer', icon: Grid2X2, category: 'layout', defaultProps: { height: 60 } },
  { type: 'stickyheader', name: 'Sticky Header', icon: Menu, category: 'layout', defaultProps: { logoUrl: '', logoText: 'Brand', bgColor: '#0f172a', links: [{ text: 'Home', url: '#' }, { text: 'Features', url: '#features' }, { text: 'Contact', url: '#contact' }], ctaText: 'Get Started', ctaUrl: '#' } },
  { type: 'icontext', name: 'Icon + Text', icon: Star, category: 'content', defaultProps: { icon: 'check', title: 'Feature Title', description: 'Feature description goes here' } },
  { type: 'columns2', name: '2 Columns', icon: Grid2X2, category: 'layout', defaultProps: { gap: 24, children: [[], []] } },
  { type: 'columns3', name: '3 Columns', icon: Grid3X3, category: 'layout', defaultProps: { gap: 24, children: [[], [], []] } },
];

const CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'text', name: 'Text' },
  { id: 'media', name: 'Media' },
  { id: 'action', name: 'Actions' },
  { id: 'conversion', name: 'Conversion' },
  { id: 'social', name: 'Social' },
  { id: 'content', name: 'Content' },
  { id: 'layout', name: 'Layout' },
];

// Droppable Canvas Area
const DroppableCanvas = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas' });
  
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[400px] transition-all ${isOver ? 'ring-2 ring-emerald-500 ring-dashed' : ''}`}
    >
      {children}
    </div>
  );
};

// Sortable Component Item
const SortableItem = ({ id, component, isSelected, onSelect, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative ${isSelected ? 'ring-2 ring-blue-500 rounded-lg' : ''}`}
      onClick={(e) => { e.stopPropagation(); onSelect(id); }}
      data-testid={`canvas-component-${id}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-2 bg-gray-800 rounded-l-lg"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      
      {/* Delete Button */}
      {isSelected && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(id); }}
          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 z-10"
          data-testid={`delete-component-${id}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
      
      <ComponentRenderer component={component} />
    </div>
  );
};

// Countdown Timer Component
const CountdownDisplay = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  
  useEffect(() => {
    const calculate = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          mins: Math.floor((diff / (1000 * 60)) % 60),
          secs: Math.floor((diff / 1000) % 60)
        });
      }
    };
    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return (
    <div className="flex justify-center gap-4">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="bg-gray-800 rounded-lg p-4 min-w-[80px]">
          <div className="text-3xl font-bold text-white">{value}</div>
          <div className="text-xs text-gray-400 capitalize">{unit}</div>
        </div>
      ))}
    </div>
  );
};

// Testimonial Carousel Component
const TestimonialCarousel = ({ testimonials, autoplay, interval }) => {
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    if (autoplay && testimonials?.length > 1) {
      const timer = setInterval(() => {
        setCurrent(c => (c + 1) % testimonials.length);
      }, interval * 1000);
      return () => clearInterval(timer);
    }
  }, [autoplay, interval, testimonials?.length]);
  
  const t = testimonials?.[current] || {};
  
  return (
    <div className="relative">
      <div className="text-center transition-all">
        <div className="flex justify-center mb-3">
          {[...Array(t.rating || 5)].map((_, i) => (
            <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
          ))}
        </div>
        <p className="text-lg text-gray-300 italic mb-4">{t.quote}</p>
        <p className="font-semibold text-white">{t.author}</p>
        <p className="text-sm text-gray-400">{t.role}</p>
      </div>
      {testimonials?.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-emerald-500 w-4' : 'bg-gray-600'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Before/After Slider Component
const BeforeAfterSlider = ({ beforeImage, afterImage, beforeLabel, afterLabel }) => {
  const [position, setPosition] = useState(50);
  
  return (
    <div className="relative overflow-hidden rounded-lg aspect-video bg-gray-800">
      {beforeImage && afterImage ? (
        <>
          <img src={afterImage} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" />
          <div 
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${position}%` }}
          >
            <img src={beforeImage} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover" style={{ width: `${10000/position}%` }} />
          </div>
          <div 
            className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
            style={{ left: `${position}%` }}
            onMouseDown={(e) => {
              const container = e.currentTarget.parentElement;
              const handleMove = (ev) => {
                const rect = container.getBoundingClientRect();
                const x = ((ev.clientX - rect.left) / rect.width) * 100;
                setPosition(Math.max(0, Math.min(100, x)));
              };
              const handleUp = () => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleUp);
              };
              document.addEventListener('mousemove', handleMove);
              document.addEventListener('mouseup', handleUp);
            }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
              <ArrowLeftRight className="w-4 h-4 text-gray-800" />
            </div>
          </div>
          <span className="absolute bottom-4 left-4 bg-black/70 px-2 py-1 rounded text-sm text-white">{beforeLabel}</span>
          <span className="absolute bottom-4 right-4 bg-black/70 px-2 py-1 rounded text-sm text-white">{afterLabel}</span>
        </>
      ) : (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-400">Add before & after images</p>
        </div>
      )}
    </div>
  );
};

// Component Renderer
const ComponentRenderer = ({ component, preview = false }) => {
  const { type, props } = component;
  const baseClass = preview ? '' : 'hover:outline hover:outline-2 hover:outline-blue-400/50 transition-all';

  switch (type) {
    case 'headline':
      const HeadlineTag = props.level || 'h1';
      return (
        <HeadlineTag
          className={`font-bold ${baseClass}`}
          style={{
            textAlign: props.align,
            color: props.color,
            fontSize: `${props.fontSize}px`,
            padding: '16px 0',
          }}
        >
          {props.text}
        </HeadlineTag>
      );

    case 'paragraph':
      return (
        <p
          className={baseClass}
          style={{
            textAlign: props.align,
            color: props.color,
            fontSize: `${props.fontSize}px`,
            lineHeight: 1.7,
            padding: '8px 0',
          }}
        >
          {props.text}
        </p>
      );

    case 'image':
      return (
        <div className={`${baseClass} p-4`}>
          {props.src ? (
            <img
              src={props.src}
              alt={props.alt}
              style={{
                width: props.width,
                borderRadius: `${props.borderRadius}px`,
                margin: '0 auto',
                display: 'block',
              }}
            />
          ) : (
            <div className="bg-gray-700 rounded-lg p-12 text-center">
              <Image className="w-12 h-12 mx-auto mb-2 text-gray-500" />
              <p className="text-gray-400">Click to add image</p>
            </div>
          )}
        </div>
      );

    case 'video':
      const getVideoEmbed = () => {
        if (!props.url) return null;
        if (props.type === 'youtube') {
          const videoId = props.url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([^&]+)/)?.[1];
          return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
        }
        if (props.type === 'vimeo') {
          const videoId = props.url.match(/vimeo\.com\/(\d+)/)?.[1];
          return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
        }
        return props.url;
      };
      return (
        <div className={`${baseClass} p-4`}>
          {props.url ? (
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getVideoEmbed()}
                className="absolute inset-0 w-full h-full rounded-lg"
                allowFullScreen
                title="Video"
              />
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-12 text-center">
              <Video className="w-12 h-12 mx-auto mb-2 text-gray-500" />
              <p className="text-gray-400">Add YouTube/Vimeo URL</p>
            </div>
          )}
        </div>
      );

    case 'button':
      return (
        <div className={`${baseClass} p-4 ${props.fullWidth ? '' : 'text-center'}`}>
          <button
            className={`px-8 py-3 rounded-lg font-semibold transition-all hover:opacity-90 ${props.fullWidth ? 'w-full' : ''} ${
              props.size === 'sm' ? 'text-sm' : props.size === 'lg' ? 'text-lg' : 'text-base'
            }`}
            style={{ backgroundColor: props.bgColor, color: props.textColor }}
          >
            {props.text}
          </button>
        </div>
      );

    case 'whatsapp':
      const waUrl = `https://wa.me/${props.phoneNumber?.replace(/\D/g, '')}?text=${encodeURIComponent(props.message || '')}`;
      return (
        <div className={`${baseClass} p-4`}>
          {props.buttonStyle === 'floating' ? (
            <div className="flex justify-end">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full shadow-lg transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                {props.buttonText}
              </a>
            </div>
          ) : (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg w-full transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              {props.buttonText}
            </a>
          )}
        </div>
      );

    case 'form':
      return (
        <div className={`${baseClass} p-6 bg-gray-800/50 rounded-xl`}>
          <div className="space-y-4 max-w-md mx-auto">
            {props.fields?.includes('name') && (
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            )}
            {props.fields?.includes('email') && (
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            )}
            {props.fields?.includes('phone') && (
              <input
                type="tel"
                placeholder="Your Phone"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            )}
            <button
              className="w-full py-3 rounded-lg font-semibold text-white"
              style={{ backgroundColor: props.buttonColor }}
            >
              {props.buttonText}
            </button>
          </div>
        </div>
      );

    case 'countdown':
      return (
        <div className={`${baseClass} p-6 text-center`}>
          <p className="text-gray-400 mb-4">{props.label}</p>
          <CountdownDisplay targetDate={props.targetDate} />
        </div>
      );

    case 'testimonial':
      return (
        <div className={`${baseClass} p-6 bg-gray-800/30 rounded-xl text-center`}>
          <div className="flex justify-center mb-3">
            {[...Array(props.rating || 5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-lg text-gray-300 italic mb-4">{props.quote}</p>
          <div className="flex items-center justify-center gap-3">
            {props.avatar && <img src={props.avatar} alt="" className="w-10 h-10 rounded-full" />}
            <div>
              <p className="font-semibold text-white">{props.author}</p>
              <p className="text-sm text-gray-400">{props.role}</p>
            </div>
          </div>
        </div>
      );

    case 'testimonialCarousel':
      return (
        <div className={`${baseClass} p-6 bg-gray-800/30 rounded-xl`}>
          <TestimonialCarousel 
            testimonials={props.testimonials} 
            autoplay={props.autoplay} 
            interval={props.interval} 
          />
        </div>
      );

    case 'pricing':
      return (
        <div className={`${baseClass} p-6`}>
          <div className="grid gap-6 md:grid-cols-3">
            {(props.plans || []).map((plan, i) => (
              <div key={i} className="bg-gray-800 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-3xl font-bold text-emerald-400 mb-4">{plan.price}</p>
                <ul className="space-y-2 mb-6">
                  {(plan.features || []).map((f, j) => (
                    <li key={j} className="text-gray-400 flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />{f}
                    </li>
                  ))}
                </ul>
                <button className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white">
                  Choose Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      );

    case 'faq':
      return (
        <div className={`${baseClass} p-6 space-y-3`}>
          {(props.items || []).map((item, i) => (
            <details key={i} className="bg-gray-800 rounded-lg group">
              <summary className="flex items-center justify-between p-4 cursor-pointer">
                <p className="font-semibold text-white">{item.question}</p>
                <ChevronDown className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" />
              </summary>
              <p className="text-gray-400 px-4 pb-4 text-sm">{item.answer}</p>
            </details>
          ))}
        </div>
      );

    case 'socialproof':
      return (
        <div className={`${baseClass} p-6 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-xl text-center`}>
          <p className="text-4xl font-bold text-white mb-1">{props.count}</p>
          <p className="text-gray-400">{props.label}</p>
        </div>
      );

    case 'googlemaps':
      return (
        <div className={`${baseClass} p-4`}>
          {props.embedUrl ? (
            <iframe
              src={props.embedUrl}
              width="100%"
              height={props.height}
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen
              loading="lazy"
              title="Google Maps"
            />
          ) : (
            <div className="bg-gray-700 rounded-lg p-12 text-center" style={{ height: props.height }}>
              <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-500" />
              <p className="text-gray-400">Add Google Maps embed URL</p>
              <p className="text-xs text-gray-500 mt-2">Go to Google Maps → Share → Embed → Copy iframe src</p>
            </div>
          )}
        </div>
      );

    case 'beforeafter':
      return (
        <div className={`${baseClass} p-4`}>
          <BeforeAfterSlider {...props} />
        </div>
      );

    case 'stickyheader':
      return (
        <div className={`${baseClass} sticky top-0 z-50`} style={{ backgroundColor: props.bgColor }}>
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {props.logoUrl ? (
                <img src={props.logoUrl} alt={props.logoText} className="h-8" />
              ) : (
                <span className="text-xl font-bold text-white">{props.logoText}</span>
              )}
            </div>
            <nav className="hidden md:flex items-center gap-6">
              {(props.links || []).map((link, i) => (
                <a key={i} href={link.url} className="text-gray-300 hover:text-white transition-colors">
                  {link.text}
                </a>
              ))}
            </nav>
            <a href={props.ctaUrl} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium">
              {props.ctaText}
            </a>
          </div>
        </div>
      );

    case 'divider':
      return (
        <div
          className={baseClass}
          style={{
            height: props.height,
            backgroundColor: props.color,
            margin: `${props.margin}px 0`,
          }}
        />
      );

    case 'spacer':
      return (
        <div
          className={`${baseClass} bg-gray-800/20`}
          style={{ height: props.height }}
        />
      );

    case 'icontext':
      return (
        <div className={`${baseClass} flex items-start gap-4 p-4`}>
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h4 className="font-semibold text-white mb-1">{props.title}</h4>
            <p className="text-gray-400 text-sm">{props.description}</p>
          </div>
        </div>
      );

    case 'columns2':
    case 'columns3':
      const cols = type === 'columns2' ? 2 : 3;
      return (
        <div
          className={`${baseClass} grid gap-6 p-4`}
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {[...Array(cols)].map((_, i) => (
            <div key={i} className="bg-gray-800/30 border-2 border-dashed border-gray-600 rounded-lg p-4 min-h-[100px] text-center">
              <p className="text-gray-500 text-sm">Column {i + 1}</p>
            </div>
          ))}
        </div>
      );

    default:
      return <div className="p-4 text-gray-500">Unknown component: {type}</div>;
  }
};

// Draggable Component from Library (Click to Add)
const DraggableComponent = ({ component, onClickAdd }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: `library-${component.type}`,
    data: { type: 'library', component },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onClickAdd(component)}
      className={`flex items-center gap-3 p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg cursor-pointer transition-colors ${
        isDragging ? 'opacity-50' : ''
      }`}
      data-testid={`component-${component.type}`}
    >
      <component.icon className="w-5 h-5 text-emerald-400" />
      <span className="text-sm text-gray-300">{component.name}</span>
      <Plus className="w-4 h-4 text-gray-500 ml-auto" />
    </div>
  );
};

// Properties Panel
const PropertiesPanel = ({ component, onChange }) => {
  const [showColorPicker, setShowColorPicker] = useState(null);

  if (!component) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 text-center p-4">
        <div>
          <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Select a component to edit its properties</p>
        </div>
      </div>
    );
  }

  const updateProp = (key, value) => {
    onChange({ ...component, props: { ...component.props, [key]: value } });
  };

  const renderColorInput = (label, propKey, defaultColor = '#ffffff') => (
    <div className="relative">
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <button
        onClick={() => setShowColorPicker(showColorPicker === propKey ? null : propKey)}
        className="w-full h-10 rounded-lg border border-gray-600 flex items-center px-3 gap-2"
      >
        <div
          className="w-6 h-6 rounded"
          style={{ backgroundColor: component.props[propKey] || defaultColor }}
        />
        <span className="text-sm text-gray-300">{component.props[propKey] || defaultColor}</span>
      </button>
      {showColorPicker === propKey && (
        <div className="absolute z-50 mt-2">
          <div className="fixed inset-0" onClick={() => setShowColorPicker(null)} />
          <div className="relative">
            <HexColorPicker
              color={component.props[propKey] || defaultColor}
              onChange={(color) => updateProp(propKey, color)}
            />
          </div>
        </div>
      )}
    </div>
  );

  const componentDef = COMPONENT_LIBRARY.find(c => c.type === component.type);

  return (
    <div className="space-y-4 p-4">
      <h3 className="font-semibold text-white flex items-center gap-2">
        <Palette className="w-4 h-4 text-emerald-400" />
        {componentDef?.name || component.type} Properties
      </h3>

      {/* Text Components */}
      {(component.type === 'headline' || component.type === 'paragraph') && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Text</label>
            <textarea
              value={component.props.text}
              onChange={(e) => updateProp('text', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          
          {component.type === 'headline' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Level</label>
              <select
                value={component.props.level}
                onChange={(e) => updateProp('level', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
              >
                <option value="h1">H1 - Large</option>
                <option value="h2">H2 - Medium</option>
                <option value="h3">H3 - Small</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">Font Size: {component.props.fontSize}px</label>
            <input
              type="range"
              min="12"
              max="72"
              value={component.props.fontSize}
              onChange={(e) => updateProp('fontSize', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Alignment</label>
            <div className="flex gap-2">
              {['left', 'center', 'right'].map((align) => (
                <button
                  key={align}
                  onClick={() => updateProp('align', align)}
                  className={`flex-1 py-2 rounded ${
                    component.props.align === align
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {align === 'left' && <AlignLeft className="w-4 h-4 mx-auto" />}
                  {align === 'center' && <AlignCenter className="w-4 h-4 mx-auto" />}
                  {align === 'right' && <AlignRight className="w-4 h-4 mx-auto" />}
                </button>
              ))}
            </div>
          </div>

          {renderColorInput('Text Color', 'color', '#ffffff')}
        </>
      )}

      {/* Image Component */}
      {component.type === 'image' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Image URL</label>
            <input
              type="url"
              value={component.props.src}
              onChange={(e) => updateProp('src', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Alt Text</label>
            <input
              type="text"
              value={component.props.alt}
              onChange={(e) => updateProp('alt', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Border Radius: {component.props.borderRadius}px</label>
            <input
              type="range"
              min="0"
              max="32"
              value={component.props.borderRadius}
              onChange={(e) => updateProp('borderRadius', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}

      {/* Video Component */}
      {component.type === 'video' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Video URL</label>
            <input
              type="url"
              value={component.props.url}
              onChange={(e) => updateProp('url', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Type</label>
            <select
              value={component.props.type}
              onChange={(e) => updateProp('type', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            >
              <option value="youtube">YouTube</option>
              <option value="vimeo">Vimeo</option>
            </select>
          </div>
        </>
      )}

      {/* Button Component */}
      {component.type === 'button' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Button Text</label>
            <input
              type="text"
              value={component.props.text}
              onChange={(e) => updateProp('text', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Link URL</label>
            <input
              type="url"
              value={component.props.url}
              onChange={(e) => updateProp('url', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          {renderColorInput('Background Color', 'bgColor', '#10b981')}
          {renderColorInput('Text Color', 'textColor', '#ffffff')}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Size</label>
            <select
              value={component.props.size}
              onChange={(e) => updateProp('size', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            >
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={component.props.fullWidth}
              onChange={(e) => updateProp('fullWidth', e.target.checked)}
              className="rounded"
            />
            <label className="text-sm text-gray-300">Full Width</label>
          </div>
        </>
      )}

      {/* WhatsApp Button */}
      {component.type === 'whatsapp' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Phone Number (with country code)</label>
            <input
              type="tel"
              value={component.props.phoneNumber}
              onChange={(e) => updateProp('phoneNumber', e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Pre-filled Message</label>
            <textarea
              value={component.props.message}
              onChange={(e) => updateProp('message', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Button Text</label>
            <input
              type="text"
              value={component.props.buttonText}
              onChange={(e) => updateProp('buttonText', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Style</label>
            <select
              value={component.props.buttonStyle}
              onChange={(e) => updateProp('buttonStyle', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            >
              <option value="floating">Floating (right aligned)</option>
              <option value="full">Full Width</option>
            </select>
          </div>
        </>
      )}

      {/* Form Component */}
      {component.type === 'form' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Form Fields</label>
            <div className="space-y-2">
              {['name', 'email', 'phone', 'message'].map((field) => (
                <label key={field} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={component.props.fields?.includes(field)}
                    onChange={(e) => {
                      const fields = e.target.checked
                        ? [...(component.props.fields || []), field]
                        : (component.props.fields || []).filter(f => f !== field);
                      updateProp('fields', fields);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-300 capitalize">{field}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Submit Button Text</label>
            <input
              type="text"
              value={component.props.buttonText}
              onChange={(e) => updateProp('buttonText', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          {renderColorInput('Button Color', 'buttonColor', '#10b981')}
        </>
      )}

      {/* Countdown */}
      {component.type === 'countdown' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Label</label>
            <input
              type="text"
              value={component.props.label}
              onChange={(e) => updateProp('label', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">End Date</label>
            <input
              type="datetime-local"
              value={component.props.targetDate?.slice(0, 16)}
              onChange={(e) => updateProp('targetDate', new Date(e.target.value).toISOString())}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {/* Testimonial */}
      {component.type === 'testimonial' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Quote</label>
            <textarea
              value={component.props.quote}
              onChange={(e) => updateProp('quote', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Author Name</label>
            <input
              type="text"
              value={component.props.author}
              onChange={(e) => updateProp('author', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Role/Company</label>
            <input
              type="text"
              value={component.props.role}
              onChange={(e) => updateProp('role', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Rating: {component.props.rating} stars</label>
            <input
              type="range"
              min="1"
              max="5"
              value={component.props.rating}
              onChange={(e) => updateProp('rating', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}

      {/* Google Maps */}
      {component.type === 'googlemaps' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Google Maps Embed URL</label>
            <input
              type="url"
              value={component.props.embedUrl}
              onChange={(e) => updateProp('embedUrl', e.target.value)}
              placeholder="https://www.google.com/maps/embed?..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Go to Google Maps → Share → Embed → Copy iframe src URL</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Height: {component.props.height}px</label>
            <input
              type="range"
              min="200"
              max="600"
              value={component.props.height}
              onChange={(e) => updateProp('height', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}

      {/* Before/After */}
      {component.type === 'beforeafter' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Before Image URL</label>
            <input
              type="url"
              value={component.props.beforeImage}
              onChange={(e) => updateProp('beforeImage', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">After Image URL</label>
            <input
              type="url"
              value={component.props.afterImage}
              onChange={(e) => updateProp('afterImage', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Before Label</label>
            <input
              type="text"
              value={component.props.beforeLabel}
              onChange={(e) => updateProp('beforeLabel', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">After Label</label>
            <input
              type="text"
              value={component.props.afterLabel}
              onChange={(e) => updateProp('afterLabel', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {/* Sticky Header */}
      {component.type === 'stickyheader' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Logo URL (optional)</label>
            <input
              type="url"
              value={component.props.logoUrl}
              onChange={(e) => updateProp('logoUrl', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Logo Text</label>
            <input
              type="text"
              value={component.props.logoText}
              onChange={(e) => updateProp('logoText', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          {renderColorInput('Background Color', 'bgColor', '#0f172a')}
          <div>
            <label className="block text-xs text-gray-400 mb-1">CTA Button Text</label>
            <input
              type="text"
              value={component.props.ctaText}
              onChange={(e) => updateProp('ctaText', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">CTA URL</label>
            <input
              type="url"
              value={component.props.ctaUrl}
              onChange={(e) => updateProp('ctaUrl', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {/* Divider */}
      {component.type === 'divider' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Height: {component.props.height}px</label>
            <input
              type="range"
              min="1"
              max="10"
              value={component.props.height}
              onChange={(e) => updateProp('height', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
          {renderColorInput('Color', 'color', '#374151')}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Margin: {component.props.margin}px</label>
            <input
              type="range"
              min="8"
              max="64"
              value={component.props.margin}
              onChange={(e) => updateProp('margin', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </>
      )}

      {/* Spacer */}
      {component.type === 'spacer' && (
        <div>
          <label className="block text-xs text-gray-400 mb-1">Height: {component.props.height}px</label>
          <input
            type="range"
            min="20"
            max="200"
            value={component.props.height}
            onChange={(e) => updateProp('height', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
      )}

      {/* Icon + Text */}
      {component.type === 'icontext' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Title</label>
            <input
              type="text"
              value={component.props.title}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Description</label>
            <textarea
              value={component.props.description}
              onChange={(e) => updateProp('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {/* Social Proof */}
      {component.type === 'socialproof' && (
        <>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Count</label>
            <input
              type="text"
              value={component.props.count}
              onChange={(e) => updateProp('count', e.target.value)}
              placeholder="10,000+"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Label</label>
            <input
              type="text"
              value={component.props.label}
              onChange={(e) => updateProp('label', e.target.value)}
              placeholder="Happy Customers"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}
    </div>
  );
};

// Page List Modal
const PageListModal = ({ isOpen, onClose, pages, onSelect, onDelete, onDuplicate }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
      >
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Folder className="w-5 h-5 text-emerald-400" />
            My Pages
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {pages.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No pages yet. Create your first page!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <div className="flex-1 cursor-pointer" onClick={() => onSelect(page)}>
                    <p className="font-medium text-white">{page.title}</p>
                    <p className="text-sm text-gray-400">
                      {page.components?.length || 0} components • {page.is_published ? '🟢 Published' : '⚪ Draft'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onDuplicate(page.id)}
                      className="p-2 text-gray-400 hover:text-white"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(page.id)}
                      className="p-2 text-gray-400 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onSelect(page)}
                      className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// AI Industry Templates
const AI_TEMPLATES = {
  real_estate: {
    name: 'Real Estate',
    icon: '🏠',
    description: 'Perfect for property listings, agents, and real estate agencies',
    colorScheme: { primary: '#10b981', secondary: '#0f766e', bg: '#0f172a' },
    pageTitle: 'Premium Property Listing',
    components: [
      { type: 'stickyheader', props: { logoText: 'Premier Properties', bgColor: '#0f172a', links: [{ text: 'Properties', url: '#properties' }, { text: 'Features', url: '#features' }, { text: 'Location', url: '#location' }, { text: 'Contact', url: '#contact' }], ctaText: 'Schedule Visit', ctaUrl: '#contact' } },
      { type: 'spacer', props: { height: 80 } },
      { type: 'headline', props: { text: 'Luxury 3BHK Apartment in Prime Location', level: 'h1', align: 'center', color: '#ffffff', fontSize: 48 } },
      { type: 'paragraph', props: { text: 'Experience modern living with stunning city views, world-class amenities, and premium finishes. Your dream home awaits.', align: 'center', color: '#94a3b8', fontSize: 18 } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'image', props: { src: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200', alt: 'Property Hero', width: '100%', borderRadius: 16 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'socialproof', props: { count: '₹1.25 Cr', label: 'Starting Price | Ready to Move' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Key Features', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'spacer', props: { height: 20 } },
      { type: 'icontext', props: { title: '3 Spacious Bedrooms', description: 'Each room with attached bathroom and premium fittings' } },
      { type: 'icontext', props: { title: 'Modern Kitchen', description: 'Fully modular kitchen with chimney and built-in appliances' } },
      { type: 'icontext', props: { title: '24/7 Security', description: 'CCTV surveillance and gated community access' } },
      { type: 'icontext', props: { title: 'Club House', description: 'Swimming pool, gym, and party hall included' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Photo Gallery', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'spacer', props: { height: 20 } },
      { type: 'image', props: { src: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800', alt: 'Living Room', width: '100%', borderRadius: 12 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Location', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: 'Prime location with easy access to schools, hospitals, shopping malls, and metro station within 5 minutes.', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'googlemaps', props: { embedUrl: '', height: 350 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'What Our Buyers Say', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'testimonialCarousel', props: { testimonials: [
        { quote: '"Best investment decision we made. The property value has already appreciated 20%!"', author: 'Rajesh Sharma', role: 'Homeowner since 2023', rating: 5 },
        { quote: '"Wonderful amenities and great community. My kids love the play area!"', author: 'Priya Patel', role: 'Resident', rating: 5 },
        { quote: '"Professional team, transparent process, and excellent after-sales support."', author: 'Amit Kumar', role: 'Investor', rating: 5 }
      ], autoplay: true, interval: 5 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Schedule a Site Visit', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: 'Fill the form below and our property expert will contact you within 24 hours.', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'form', props: { fields: ['name', 'email', 'phone'], buttonText: 'Book Site Visit', buttonColor: '#10b981' } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'whatsapp', props: { phoneNumber: '+919876543210', message: 'Hi! I am interested in the 3BHK property listing.', buttonText: 'WhatsApp Us', buttonStyle: 'full' } },
      { type: 'spacer', props: { height: 60 } },
    ]
  },
  insurance: {
    name: 'Insurance',
    icon: '🛡️',
    description: 'Build trust and generate leads for insurance products',
    colorScheme: { primary: '#3b82f6', secondary: '#1e40af', bg: '#0f172a' },
    pageTitle: 'Insurance Plans',
    components: [
      { type: 'stickyheader', props: { logoText: 'SecureLife Insurance', bgColor: '#0f172a', links: [{ text: 'Plans', url: '#plans' }, { text: 'Benefits', url: '#benefits' }, { text: 'FAQ', url: '#faq' }, { text: 'Get Quote', url: '#quote' }], ctaText: 'Get Free Quote', ctaUrl: '#quote' } },
      { type: 'spacer', props: { height: 80 } },
      { type: 'headline', props: { text: 'Protect What Matters Most', level: 'h1', align: 'center', color: '#ffffff', fontSize: 48 } },
      { type: 'paragraph', props: { text: 'Comprehensive insurance coverage for you and your family. Get peace of mind with our trusted protection plans.', align: 'center', color: '#94a3b8', fontSize: 18 } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'socialproof', props: { count: '50,000+', label: 'Happy Customers | ₹500 Cr+ Claims Settled' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Why Choose Us?', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'spacer', props: { height: 20 } },
      { type: 'icontext', props: { title: '98% Claim Settlement', description: 'Industry-leading claim settlement ratio with quick processing' } },
      { type: 'icontext', props: { title: 'No Medical Check-up', description: 'Instant policy for ages 18-45 with no medical examination' } },
      { type: 'icontext', props: { title: 'Tax Benefits', description: 'Save up to ₹46,800 under Section 80C & 80D' } },
      { type: 'icontext', props: { title: '24/7 Support', description: 'Round-the-clock customer support and claim assistance' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Coverage Comparison', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'beforeafter', props: { beforeImage: '', afterImage: '', beforeLabel: 'Without Insurance', afterLabel: 'With Our Coverage' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Customer Success Stories', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'testimonialCarousel', props: { testimonials: [
        { quote: '"Claim was settled within 48 hours. The team was incredibly supportive during a difficult time."', author: 'Meera Reddy', role: 'Policy Holder', rating: 5 },
        { quote: '"Best premium rates I found after comparing 10+ companies. Highly recommended!"', author: 'Vikram Singh', role: 'Business Owner', rating: 5 },
        { quote: '"The online process was so smooth. Got my policy in just 10 minutes!"', author: 'Sneha Joshi', role: 'IT Professional', rating: 5 }
      ], autoplay: true, interval: 5 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Frequently Asked Questions', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'faq', props: { items: [
        { question: 'What is the minimum age to buy a policy?', answer: 'You can purchase our policies from age 18 to 65. Coverage continues till age 99.' },
        { question: 'How quickly are claims processed?', answer: 'Most claims are processed within 48-72 hours. Critical illness claims may take up to 7 days for verification.' },
        { question: 'Can I cancel my policy?', answer: 'Yes, you can cancel within the 15-day free-look period for a full refund. After that, surrender value applies.' },
        { question: 'Is there a waiting period?', answer: 'There is a 90-day waiting period for illness-related claims. Accidents are covered from day one.' }
      ] } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Get Your Free Quote', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: 'Fill the form and our insurance expert will call you with a personalized quote.', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'form', props: { fields: ['name', 'email', 'phone'], buttonText: 'Get Free Quote', buttonColor: '#3b82f6' } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'whatsapp', props: { phoneNumber: '+919876543210', message: 'Hi! I want to know more about insurance plans.', buttonText: 'Chat with Expert', buttonStyle: 'full' } },
      { type: 'spacer', props: { height: 60 } },
    ]
  },
  training: {
    name: 'Training & Education',
    icon: '📚',
    description: 'Courses, workshops, and educational programs',
    colorScheme: { primary: '#8b5cf6', secondary: '#6d28d9', bg: '#0f172a' },
    pageTitle: 'Professional Training Course',
    components: [
      { type: 'stickyheader', props: { logoText: 'SkillMaster Academy', bgColor: '#0f172a', links: [{ text: 'Curriculum', url: '#curriculum' }, { text: 'Instructor', url: '#instructor' }, { text: 'Reviews', url: '#reviews' }, { text: 'Enroll', url: '#enroll' }], ctaText: 'Enroll Now', ctaUrl: '#enroll' } },
      { type: 'spacer', props: { height: 80 } },
      { type: 'headline', props: { text: 'Master Full-Stack Development in 12 Weeks', level: 'h1', align: 'center', color: '#ffffff', fontSize: 44 } },
      { type: 'paragraph', props: { text: 'Learn from industry experts, build real projects, and land your dream job with our placement assistance program.', align: 'center', color: '#94a3b8', fontSize: 18 } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'video', props: { url: '', type: 'youtube', autoplay: false } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'countdown', props: { targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), label: 'Early Bird Discount Ends In' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'socialproof', props: { count: '5,000+', label: 'Students Trained | 95% Placement Rate' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Course Curriculum', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'spacer', props: { height: 20 } },
      { type: 'icontext', props: { title: 'Week 1-3: Frontend Fundamentals', description: 'HTML5, CSS3, JavaScript ES6+, React.js with hooks and state management' } },
      { type: 'icontext', props: { title: 'Week 4-6: Backend Development', description: 'Node.js, Express, REST APIs, Authentication, Database design' } },
      { type: 'icontext', props: { title: 'Week 7-9: Database & Cloud', description: 'MongoDB, PostgreSQL, AWS deployment, Docker basics' } },
      { type: 'icontext', props: { title: 'Week 10-12: Projects & Placement', description: 'Build 3 portfolio projects, mock interviews, resume building' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Meet Your Instructor', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'testimonial', props: { quote: '"I have 10+ years of experience at Google, Amazon, and startups. My mission is to help you become job-ready with practical skills."', author: 'Rahul Verma', role: 'Senior Software Engineer, Ex-Google', rating: 5, avatar: '' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Student Success Stories', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'testimonialCarousel', props: { testimonials: [
        { quote: '"Got placed at Microsoft within 2 weeks of completing the course! The projects really helped."', author: 'Ankit Sharma', role: 'SDE at Microsoft', rating: 5 },
        { quote: '"As a non-tech background person, I was scared. But the step-by-step approach made it easy."', author: 'Neha Gupta', role: 'Frontend Developer at Flipkart', rating: 5 },
        { quote: '"Best investment I made in my career. Salary jumped from 4L to 18L after this course."', author: 'Karan Mehta', role: 'Full-Stack Developer', rating: 5 }
      ], autoplay: true, interval: 5 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Upcoming Batch Schedule', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: '📅 Next Batch: 15th of this month | ⏰ Timing: 7PM - 9PM (Weekdays) | 💻 Mode: Live Online', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'pricing', props: { plans: [
        { name: 'Self-Paced', price: '₹14,999', features: ['Recorded videos', 'Project files', 'Certificate', '6 months access'] },
        { name: 'Live Batch', price: '₹29,999', features: ['Live classes', 'Doubt sessions', 'Projects review', 'Placement prep', '1 year access'] },
        { name: 'Pro + Mentorship', price: '₹49,999', features: ['Everything in Live', '1-on-1 mentoring', 'Guaranteed placement', 'Lifetime access'] }
      ] } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Enroll Now', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: 'Limited seats available. Reserve your spot today!', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'form', props: { fields: ['name', 'email', 'phone'], buttonText: 'Reserve My Seat', buttonColor: '#8b5cf6' } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'whatsapp', props: { phoneNumber: '+919876543210', message: 'Hi! I want to know more about the Full-Stack Development course.', buttonText: 'Ask Questions on WhatsApp', buttonStyle: 'full' } },
      { type: 'spacer', props: { height: 60 } },
    ]
  },
  general: {
    name: 'General Business',
    icon: '💼',
    description: 'Versatile template for any business type',
    colorScheme: { primary: '#10b981', secondary: '#059669', bg: '#0f172a' },
    pageTitle: 'Business Landing Page',
    components: [
      { type: 'stickyheader', props: { logoText: 'Your Brand', bgColor: '#0f172a', links: [{ text: 'Services', url: '#services' }, { text: 'About', url: '#about' }, { text: 'Testimonials', url: '#testimonials' }, { text: 'Contact', url: '#contact' }], ctaText: 'Get Started', ctaUrl: '#contact' } },
      { type: 'spacer', props: { height: 80 } },
      { type: 'headline', props: { text: 'Transform Your Business Today', level: 'h1', align: 'center', color: '#ffffff', fontSize: 48 } },
      { type: 'paragraph', props: { text: 'We help businesses grow with innovative solutions, expert guidance, and proven strategies that deliver results.', align: 'center', color: '#94a3b8', fontSize: 18 } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'button', props: { text: 'Schedule Free Consultation', url: '#contact', bgColor: '#10b981', textColor: '#ffffff', size: 'lg', fullWidth: false } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'socialproof', props: { count: '500+', label: 'Businesses Served | 10+ Years Experience' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Our Services', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'spacer', props: { height: 20 } },
      { type: 'icontext', props: { title: 'Strategic Consulting', description: 'Expert advice to help you make better business decisions' } },
      { type: 'icontext', props: { title: 'Digital Marketing', description: 'Reach more customers with targeted marketing campaigns' } },
      { type: 'icontext', props: { title: 'Process Automation', description: 'Streamline operations and save time with smart automation' } },
      { type: 'icontext', props: { title: 'Growth Support', description: 'Ongoing support to help you scale your business' } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'What Our Clients Say', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'testimonialCarousel', props: { testimonials: [
        { quote: '"They helped us increase revenue by 200% in just 6 months. Incredible results!"', author: 'Business Owner', role: 'E-commerce', rating: 5 },
        { quote: '"Professional team that truly understands business challenges. Highly recommended!"', author: 'CEO', role: 'Tech Startup', rating: 5 },
        { quote: '"The ROI on their services has been exceptional. Best decision we made."', author: 'Director', role: 'Manufacturing', rating: 5 }
      ], autoplay: true, interval: 5 } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Frequently Asked Questions', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'faq', props: { items: [
        { question: 'How long does it take to see results?', answer: 'Most clients start seeing measurable results within 30-60 days of implementation.' },
        { question: 'What industries do you work with?', answer: 'We work with businesses across all industries including retail, healthcare, tech, and manufacturing.' },
        { question: 'Do you offer custom solutions?', answer: 'Yes, every solution is tailored to your specific business needs and goals.' }
      ] } },
      { type: 'spacer', props: { height: 60 } },
      { type: 'headline', props: { text: 'Get In Touch', level: 'h2', align: 'center', color: '#ffffff', fontSize: 36 } },
      { type: 'paragraph', props: { text: 'Ready to grow your business? Fill out the form and we\'ll contact you within 24 hours.', align: 'center', color: '#94a3b8', fontSize: 16 } },
      { type: 'form', props: { fields: ['name', 'email', 'phone', 'message'], buttonText: 'Send Message', buttonColor: '#10b981' } },
      { type: 'spacer', props: { height: 40 } },
      { type: 'whatsapp', props: { phoneNumber: '+919876543210', message: 'Hi! I am interested in learning more about your services.', buttonText: 'Chat on WhatsApp', buttonStyle: 'full' } },
      { type: 'spacer', props: { height: 60 } },
    ]
  }
};

// AI Template Modal
const AITemplateModal = ({ isOpen, onClose, onApplyTemplate }) => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  if (!isOpen) return null;

  const handleApply = async () => {
    if (!selectedIndustry) return;
    
    setIsGenerating(true);
    
    // Simulate AI processing delay for better UX
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const template = AI_TEMPLATES[selectedIndustry];
    onApplyTemplate(template);
    setIsGenerating(false);
    setSelectedIndustry(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
      >
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Start with AI</h2>
                <p className="text-sm text-gray-400">Select your industry for a custom page template</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-2">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(AI_TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                onClick={() => setSelectedIndustry(key)}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  selectedIndustry === key
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
                data-testid={`ai-template-${key}`}
              >
                <div className="text-3xl mb-3">{template.icon}</div>
                <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                <p className="text-sm text-gray-400">{template.description}</p>
                {selectedIndustry === key && (
                  <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
                    <Check className="w-4 h-4" />
                    Selected
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {selectedIndustry && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-gray-800/50 border-t border-gray-700"
          >
            <div className="mb-4">
              <h3 className="font-semibold text-white mb-2">Template Preview</h3>
              <div className="flex flex-wrap gap-2">
                {AI_TEMPLATES[selectedIndustry].components.slice(0, 8).map((comp, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300 capitalize">
                    {comp.type.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                ))}
                {AI_TEMPLATES[selectedIndustry].components.length > 8 && (
                  <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-400">
                    +{AI_TEMPLATES[selectedIndustry].components.length - 8} more
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleApply}
                disabled={isGenerating}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                data-testid="apply-ai-template-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Page...
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Apply Template
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedIndustry(null)}
                className="border-gray-600 text-gray-300"
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// Main Page Builder Component
const PageBuilder = () => {
  const [components, setComponents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('desktop');
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [pages, setPages] = useState([]);
  const [showPageList, setShowPageList] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    title: 'Untitled Page',
    bgColor: '#0f172a',
  });
  const autoSaveRef = useRef(null);
  const token = localStorage.getItem('auth_token');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const selectedComponent = components.find(c => c.id === selectedId);

  // Fetch pages on mount
  useEffect(() => {
    fetchPages();
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (hasUnsavedChanges && currentPageId) {
      autoSaveRef.current = setTimeout(() => {
        savePage(true);
      }, 30000);
      
      return () => clearTimeout(autoSaveRef.current);
    }
  }, [hasUnsavedChanges, components, currentPageId]);

  // Track unsaved changes
  useEffect(() => {
    if (history.length > 1) {
      setHasUnsavedChanges(true);
    }
  }, [components]);

  const fetchPages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    }
  };

  const addToHistory = useCallback((newComponents) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newComponents]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setComponents([...history[historyIndex - 1]]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setComponents([...history[historyIndex + 1]]);
    }
  };

  // Click to add component
  const handleClickAdd = (libComponent) => {
    const newComponent = {
      id: `${libComponent.type}-${Date.now()}`,
      type: libComponent.type,
      props: { ...libComponent.defaultProps },
    };
    
    const newComponents = [...components, newComponent];
    setComponents(newComponents);
    addToHistory(newComponents);
    setSelectedId(newComponent.id);
    toast.success(`Added ${libComponent.name}`);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    // Adding new component from library
    if (active.id.toString().startsWith('library-')) {
      const componentType = active.id.toString().replace('library-', '');
      const libComponent = COMPONENT_LIBRARY.find(c => c.type === componentType);
      
      if (libComponent) {
        const newComponent = {
          id: `${componentType}-${Date.now()}`,
          type: componentType,
          props: { ...libComponent.defaultProps },
        };
        
        const newComponents = [...components, newComponent];
        setComponents(newComponents);
        addToHistory(newComponents);
        setSelectedId(newComponent.id);
      }
      return;
    }

    // Reordering existing components
    if (active.id !== over.id && !over.id.toString().startsWith('library-')) {
      const oldIndex = components.findIndex(c => c.id === active.id);
      const newIndex = components.findIndex(c => c.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newComponents = arrayMove(components, oldIndex, newIndex);
        setComponents(newComponents);
        addToHistory(newComponents);
      }
    }
  };

  const deleteComponent = (id) => {
    const newComponents = components.filter(c => c.id !== id);
    setComponents(newComponents);
    addToHistory(newComponents);
    setSelectedId(null);
    toast.success('Component deleted');
  };

  const updateComponent = (updatedComponent) => {
    const newComponents = components.map(c =>
      c.id === updatedComponent.id ? updatedComponent : c
    );
    setComponents(newComponents);
    setHasUnsavedChanges(true);
  };

  const savePage = async (isAutoSave = false) => {
    setSaving(true);
    try {
      const pageData = {
        title: pageSettings.title,
        components: components.map(c => ({ id: c.id, type: c.type, props: c.props })),
        settings: pageSettings
      };

      let res;
      if (currentPageId) {
        // Update existing page
        res = await fetch(`${API_URL}/api/pages/${currentPageId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(pageData)
        });
      } else {
        // Create new page
        res = await fetch(`${API_URL}/api/pages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(pageData)
        });
      }

      if (res.ok) {
        const data = await res.json();
        if (!currentPageId && data.page?.id) {
          setCurrentPageId(data.page.id);
        }
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
        fetchPages();
        if (!isAutoSave) {
          toast.success('Page saved!');
        }
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      if (!isAutoSave) {
        toast.error('Failed to save page');
      }
    } finally {
      setSaving(false);
    }
  };

  const loadPage = (page) => {
    setCurrentPageId(page.id);
    setComponents(page.components || []);
    setPageSettings({
      title: page.title,
      bgColor: page.settings?.bgColor || '#0f172a'
    });
    setHistory([page.components || []]);
    setHistoryIndex(0);
    setHasUnsavedChanges(false);
    setShowPageList(false);
    toast.success(`Loaded: ${page.title}`);
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchPages();
        if (currentPageId === pageId) {
          createNewPage();
        }
        toast.success('Page deleted');
      }
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const duplicatePage = async (pageId) => {
    try {
      const res = await fetch(`${API_URL}/api/pages/${pageId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchPages();
        toast.success('Page duplicated');
      }
    } catch (error) {
      toast.error('Failed to duplicate page');
    }
  };

  const createNewPage = () => {
    setCurrentPageId(null);
    setComponents([]);
    setPageSettings({ title: 'Untitled Page', bgColor: '#0f172a' });
    setHistory([[]]);
    setHistoryIndex(0);
    setHasUnsavedChanges(false);
    setShowPageList(false);
  };

  // Apply AI template
  const applyAITemplate = (template) => {
    const newComponents = template.components.map((comp, index) => ({
      id: `${comp.type}-${Date.now()}-${index}`,
      type: comp.type,
      props: { ...comp.props }
    }));
    
    setComponents(newComponents);
    setPageSettings({
      title: template.pageTitle,
      bgColor: template.colorScheme.bg
    });
    addToHistory(newComponents);
    setHasUnsavedChanges(true);
    toast.success(`Applied ${template.name} template with ${newComponents.length} components!`);
  };

  // Save current page as custom template
  const saveAsTemplate = async () => {
    if (components.length === 0) {
      toast.error('Add some components first');
      return;
    }
    
    const templateName = prompt('Enter template name:');
    if (!templateName) return;
    
    try {
      const templateData = {
        name: templateName,
        components: components.map(c => ({ type: c.type, props: c.props })),
        settings: pageSettings,
        created_at: new Date().toISOString()
      };
      
      // Save to localStorage for now (could be extended to backend)
      const customTemplates = JSON.parse(localStorage.getItem('custom_page_templates') || '[]');
      customTemplates.push(templateData);
      localStorage.setItem('custom_page_templates', JSON.stringify(customTemplates));
      
      toast.success('Template saved!');
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const publishPage = async () => {
    if (!currentPageId) {
      await savePage();
    }
    
    try {
      const res = await fetch(`${API_URL}/api/pages/${currentPageId}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchPages();
        toast.success('Page published!');
      }
    } catch (error) {
      toast.error('Failed to publish page');
    }
  };

  const getCanvasWidth = () => {
    switch (viewMode) {
      case 'tablet': return 'max-w-2xl';
      case 'mobile': return 'max-w-sm';
      default: return 'max-w-4xl';
    }
  };

  const filteredComponents = activeCategory === 'all' 
    ? COMPONENT_LIBRARY 
    : COMPONENT_LIBRARY.filter(c => c.category === activeCategory);

  return (
    <div className="h-screen flex flex-col bg-gray-950" data-testid="page-builder">
      {/* Top Toolbar */}
      <div className="h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="text-gray-400"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPageList(true)}
            className="text-gray-400"
            data-testid="open-pages-btn"
          >
            <Folder className="w-4 h-4 mr-1" />
            Pages
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={createNewPage}
            className="text-emerald-400"
            data-testid="new-page-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAIModal(true)}
            className="text-purple-400 hover:text-purple-300"
            data-testid="ai-template-btn"
          >
            <Star className="w-4 h-4 mr-1" />
            AI Template
          </Button>
          <div className="h-6 w-px bg-gray-700" />
          <input
            type="text"
            value={pageSettings.title}
            onChange={(e) => {
              setPageSettings({ ...pageSettings, title: e.target.value });
              setHasUnsavedChanges(true);
            }}
            className="bg-transparent text-white font-semibold border-none outline-none"
            data-testid="page-title-input"
          />
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Unsaved
            </span>
          )}
          {lastSaved && !hasUnsavedChanges && (
            <span className="text-xs text-gray-500">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <Button
            variant="ghost"
            size="sm"
            onClick={undo}
            disabled={historyIndex === 0}
            className="text-gray-400"
            data-testid="undo-btn"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={redo}
            disabled={historyIndex === history.length - 1}
            className="text-gray-400"
            data-testid="redo-btn"
          >
            <Redo2 className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-gray-700 mx-2" />

          {/* Viewport Toggle */}
          <div className="flex bg-gray-800 rounded-lg p-1">
            {[
              { mode: 'desktop', icon: Monitor },
              { mode: 'tablet', icon: Tablet },
              { mode: 'mobile', icon: Smartphone },
            ].map(({ mode, icon: Icon }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`p-2 rounded ${
                  viewMode === mode
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`viewport-${mode}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-700 mx-2" />

          {/* Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={publishPage}
            className="border-gray-600 text-gray-300"
            data-testid="publish-btn"
          >
            <Globe className="w-4 h-4 mr-1" />
            Publish
          </Button>
          <Button
            size="sm"
            onClick={() => savePage(false)}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* Components Panel (Left) */}
          <div className="w-64 bg-gray-900 border-r border-gray-700 flex flex-col">
            <div className="p-3 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Layout className="w-4 h-4 text-emerald-400" />
                Components
              </h3>
              <p className="text-xs text-gray-500 mt-1">Click or drag to add</p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-1 p-2 border-b border-gray-700">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-2 py-1 text-xs rounded ${
                    activeCategory === cat.id
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Component List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              <SortableContext items={filteredComponents.map(c => `library-${c.type}`)}>
                {filteredComponents.map((comp) => (
                  <DraggableComponent 
                    key={comp.type} 
                    component={comp} 
                    onClickAdd={handleClickAdd}
                  />
                ))}
              </SortableContext>
            </div>
          </div>

          {/* Canvas (Center) */}
          <div 
            className="flex-1 overflow-y-auto p-8"
            style={{ backgroundColor: pageSettings.bgColor }}
            onClick={() => setSelectedId(null)}
            data-testid="canvas-area"
          >
            <DroppableCanvas>
              <div className={`${getCanvasWidth()} mx-auto transition-all`}>
                {components.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-600 rounded-xl p-12 text-center">
                    <Plus className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                    <p className="text-gray-400 mb-2">Click or drag components here</p>
                    <p className="text-sm text-gray-500 mb-6">
                      Select from the left panel to start building
                    </p>
                    <div className="flex justify-center gap-3">
                      <Button
                        onClick={() => setShowAIModal(true)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                        data-testid="start-with-ai-btn"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Start with AI
                      </Button>
                    </div>
                  </div>
                ) : (
                  <SortableContext
                    items={components.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {components.map((comp) => (
                        <SortableItem
                          key={comp.id}
                          id={comp.id}
                          component={comp}
                          isSelected={selectedId === comp.id}
                          onSelect={setSelectedId}
                          onDelete={deleteComponent}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}
              </div>
            </DroppableCanvas>
          </div>
        </DndContext>

        {/* Properties Panel (Right) */}
        <div className="w-72 bg-gray-900 border-l border-gray-700 overflow-y-auto">
          <div className="p-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Settings className="w-4 h-4 text-emerald-400" />
              Properties
            </h3>
          </div>
          <PropertiesPanel
            component={selectedComponent}
            onChange={updateComponent}
          />
        </div>
      </div>

      {/* Page List Modal */}
      <PageListModal
        isOpen={showPageList}
        onClose={() => setShowPageList(false)}
        pages={pages}
        onSelect={loadPage}
        onDelete={deletePage}
        onDuplicate={duplicatePage}
      />

      {/* AI Template Modal */}
      <AITemplateModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onApplyTemplate={applyAITemplate}
      />
    </div>
  );
};

export default PageBuilder;
